import { DateTime } from '@ksyos/date-time';
import { Box, Collapse, Divider } from '@ksyos/design-system';
import { useState } from 'react';
import type { ICustomControlDiagnosticReportReadProps } from '@frontend/components/pages/customControls/interfaces/customControlDiagnosticReportReadProps';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { GradingSummary } from '@common/enums/gradingSummary';
import { getDisplayName } from '@common/utils/getDisplayName';
import { AddendumButton } from '@frontend/components/elements/carePathButtons/AddendumButton';
import { DiagnosticReportReadButtons } from '@frontend/components/elements/carePathButtons/DiagnosticReportReadButtons';
import { ProcessContainer } from '@frontend/components/pages/episodes/common';
import { ProcessItemFooter } from '@frontend/components/pages/episodes/ProcessItemFooter';
import { ProcessItemHeader } from '@frontend/components/pages/episodes/ProcessItemHeader';
import { ReloadEpisode } from '@frontend/components/pages/episodes/reloadEpisode';
import { getSelectedLanguage } from '@frontend/translate';
import { TcohV2DiagnosticReportAddendumAdd } from './TcohV2DiagnosticReportAddendumAdd';
import { TcohV2DiagnosticReportAddendumRead } from './TcohV2DiagnosticReportAddendumRead';
import { TcohV2DiagnosticReportReadCustomControlFields } from './TcohV2DiagnosticReportReadCustomControlFields';
import { TcohV2DiagnosticReportSummary } from './TcohV2DiagnosticReportSummary';

export function TcohV2DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    const translate = getSelectedLanguage();

    const [isAddingAddendum, setIsAddingAddendum] = useState(false);

    return (
        <ProcessContainer>
            <Box
                display="flex"
                flexDirection={'column'}
                gap={8}
            >
                <Box>
                    <ProcessItemHeader
                        date={props.diagnosticReport && props.diagnosticReport.dateSent}
                        isFirstItem={props.isFirstItem}
                        isItemOpen={props.itemState.isOpen}
                        onToggle={async (isOpened) =>
                            await props.updateItemState({ ...props.itemState, isOpen: isOpened })
                        }
                        organizationName={props.diagnosticReport.performerOrganization?.name}
                        practitionerName={
                            props.diagnosticReport.performerPractitioner
                                ? getDisplayName(props.diagnosticReport.performerPractitioner)
                                : undefined
                        }
                        process={props.process}
                        status={getStatus()}
                        title={getServiceLabel(
                            props.process.code,
                            'diagnosticReport',
                            translate.diagnosticReport.category_examinationReport
                        )}
                    />
                    <TcohV2DiagnosticReportSummary {...props} />
                    <Collapse isOpen={props.itemState.isOpen}>
                        {checkIsFooterVisible() && <Divider />}

                        <TcohV2DiagnosticReportReadCustomControlFields
                            customControlData={props.diagnosticReport.customControlData}
                            txcsProcesId={props.request.txcsProcesId}
                        />
                    </Collapse>
                    {checkIsFooterVisible() && (
                        <ProcessItemFooter
                            isItemOpen={props.itemState.isOpen}
                            labelLess={translate.diagnosticReport.hideExaminationReport}
                            labelMore={translate.diagnosticReport.showExaminationReport}
                            onToggle={async (isOpened) =>
                                await props.updateItemState({ ...props.itemState, isOpen: isOpened })
                            }
                        />
                    )}
                    {props.diagnosticReport.customControlData.addendum && (
                        <Box
                            paddingBottom={4}
                            paddingTop={4}
                        >
                            <TcohV2DiagnosticReportAddendumRead diagnosticReport={props.diagnosticReport} />
                        </Box>
                    )}
                    {!isAddingAddendum && (
                        <DiagnosticReportReadButtons
                            {...props}
                            customButtonsLeft={
                                !props.diagnosticReport.customControlData.addendum
                                    ? [
                                          <AddendumButton
                                              dateSent={
                                                  props.diagnosticReport.dateSent
                                                      ? DateTime.from(props.diagnosticReport.dateSent)
                                                      : undefined
                                              }
                                              key="addendumButton"
                                              onAddAddendum={() => setIsAddingAddendum(true)}
                                              performerPractitionerId={props.diagnosticReport.performerPractitionerId}
                                          />,
                                      ]
                                    : undefined
                            }
                        />
                    )}
                </Box>
                {isAddingAddendum && (
                    <TcohV2DiagnosticReportAddendumAdd
                        diagnosticReport={props.diagnosticReport}
                        onCancel={() => setIsAddingAddendum(false)}
                        onSaved={async () => {
                            setIsAddingAddendum(false);
                            await props.reloadProcess?.(ReloadEpisode.reloadAll);
                        }}
                    />
                )}
            </Box>
        </ProcessContainer>
    );

    function checkIsFooterVisible() {
        return Boolean(
            props.diagnosticReport.customControlData.relatieEcgEnKlachten ||
                props.diagnosticReport.customControlData.overigeOpmerkingen
        );
    }

    function getStatus() {
        const gradingSummary = (() => {
            const originalGradingSummary = props.diagnosticReport.gradingSummary;
            const addendumGradingSummary = props.diagnosticReport.customControlData?.addendum?.gradingSummary;

            return addendumGradingSummary ? addendumGradingSummary : originalGradingSummary;
        })();

        return gradingSummary === GradingSummary.deviant ? translate.diagnosis.deviant : undefined;
    }
}
